// Central query-key factory. One place so invalidation can't drift from the
// keys that queries register under.

export const queryKeys = {
  meetings: {
    all: ['meetings'] as const,
    list: () => [...queryKeys.meetings.all, 'list'] as const,
    /** `scope` distinguishes the JWT-scoped `/mine` result from the public
     *  list + local-id filter, so signing in/out never serves a stale cache
     *  from the other mode. */
    recent: (scope: string) => [...queryKeys.meetings.all, 'recent', scope] as const,
    detail: (meetingId: string) => [...queryKeys.meetings.all, 'detail', meetingId] as const,
    /** The join gate is keyed by role + session so two tabs never share a result. */
    join: (meetingId: string, as: string, sessionId: string) =>
      [...queryKeys.meetings.all, 'join', meetingId, as, sessionId] as const,
    /** A lead's own waiting-room status — keyed by session so two tabs never share it. */
    admission: (meetingId: string, sessionId: string) =>
      [...queryKeys.meetings.all, 'admission', meetingId, sessionId] as const,
    /** Host-only — everyone currently waiting for this meeting. */
    admissions: (meetingId: string) =>
      [...queryKeys.meetings.all, 'admissions', meetingId] as const,
  },
  notes: {
    all: ['notes'] as const,
    detail: (meetingId: string) => [...queryKeys.notes.all, meetingId] as const,
  },
  analytics: {
    all: ['analytics'] as const,
    detail: (meetingId: string) => [...queryKeys.analytics.all, meetingId] as const,
  },
  /** Room-Provider gate (nsi-sales SSO integration, /api/rooms/*) — separate
   *  key space from `meetings`, since a roomId is a different resource. */
  rooms: {
    all: ['rooms'] as const,
    hostJoin: (roomId: string) => [...queryKeys.rooms.all, 'host-join', roomId] as const,
    guestJoin: (roomId: string, t: string) =>
      [...queryKeys.rooms.all, 'guest-join', roomId, t] as const,
    /** Host-only — everyone currently waiting to be admitted into this room. */
    admissions: (roomId: string) => [...queryKeys.rooms.all, 'admissions', roomId] as const,
    /** JWT-scoped "my meetings," keyed by status filter so tabs don't share a cache entry. */
    mine: (status: string) => [...queryKeys.rooms.all, 'mine', status] as const,
    /** Host-only analytics/notes (Meeting/Room unification, nsimeet-direct rooms only). */
    analytics: (roomId: string) => [...queryKeys.rooms.all, 'analytics', roomId] as const,
    recording: (roomId: string) => [...queryKeys.rooms.all, 'recording', roomId] as const,
    transcript: (roomId: string) => [...queryKeys.rooms.all, 'transcript', roomId] as const,
    /** Rep-only post-call material (WhatsApp follow-up draft) — deliberately
     *  a separate endpoint from analytics, which is also pushed to unsigned
     *  per-room CRM webhooks. */
    callBrief: (roomId: string) => [...queryKeys.rooms.all, 'call-brief', roomId] as const,
  },
  /** AI closing-coach knowledge base (RAG) + suggestion review queue. */
  knowledge: {
    all: ['knowledge'] as const,
    list: (category?: string) => [...queryKeys.knowledge.all, 'list', category ?? 'all'] as const,
    candidates: (source?: string) =>
      [...queryKeys.knowledge.all, 'candidates', source ?? 'all'] as const,
  },
} as const;
